import type { QuestionTypes } from '@/types';
import { useTranslation } from 'react-i18next';
import { useFormResponseStore } from '@/store';
import React, { useEffect, useState } from 'react';
import { TimePicker } from '@/components/ui/time-picker';
import { formatInTimezone, parseDateInTimezone } from '@/utils/timezone';

interface DateProps {
    question: QuestionTypes;
    sectionId: string;
    sectionName: string;
    setValidationErrors: (sectionId: string, questionId: string, error: string | null) => void;
}

export const TimeAnswer: React.FC<DateProps> = ({ question, sectionId, sectionName, setValidationErrors }) => {
    const { t } = useTranslation();
    const { setResponse, formResponses } = useFormResponseStore();
    const existingSection = formResponses[sectionId];
    const existingResponse = existingSection?.questions[question.id]?.response || null;

    const [timeValue, setTimeValue] = useState<string>(() => {
        if (existingResponse) {
            if (typeof existingResponse === 'string') {
                return existingResponse;
            } else if (existingResponse.time) {
                return existingResponse.time;
            }
        }
        return '';
    });

    // Initial validation on mount - runs ONCE like other components
    useEffect(() => {
        if (question.required === 'yes') {
            if (!timeValue) {
                setValidationErrors(sectionId, question.id, `${question.label} ${t('forms.isRequired')}`);
            } else {
                setValidationErrors(sectionId, question.id, null);
            }
        }
    }, []); // Empty deps - runs only on mount

    useEffect(() => {
        if (timeValue) {
            const response = timeValue;
            setResponse(sectionId, sectionName, question.id, question.type, question.label ?? '', response);
        }
    }, [timeValue]);

    const handleTimeChange = (value: string) => {
        setTimeValue(value);
        // Clear validation error when time is selected
        if (value) {
            setValidationErrors(sectionId, question.id, null);
        }
    };

    const formatTimeForDisplay = (timeValue: string) => {
        if (!timeValue || !question.timezone) return timeValue;
        
        try {
            // Parse the time value and format it in the specified timezone
            const now = new Date();
            const [hours, minutes] = timeValue.split(':');
            const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parseInt(hours), parseInt(minutes));
            return formatInTimezone(date, question.timezone, { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
            });
        } catch {
            return timeValue;
        }
    };

    const getCurrentTime = () => {
        const now = new Date();
        return now.toLocaleTimeString('en-US', { 
            hour12: true, 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const getMidnightTime = () => {
        const midnight = new Date();
        midnight.setHours(23, 59, 0, 0);
        return midnight.toLocaleTimeString('en-US', { 
            hour12: true, 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const renderTimeInput = () => {
        switch (question.timeType) {
            case 'all-time':
                return (
                    <TimePicker
                        value={timeValue}
                        onChange={handleTimeChange}
                        placeholder={t('forms.pickATime')}
                        format="12h"
                        timezone={question.timezone}
                        className="mt-1"
                    />
                );
            case 'past-only':
                return (
                    <TimePicker
                        value={timeValue}
                        onChange={handleTimeChange}
                        placeholder={t('forms.pickATime')}
                        format="12h"
                        maxTime={getCurrentTime()}
                        timezone={question.timezone}
                        className="mt-1"
                    />
                );
            case 'future-only':
                return (
                    <TimePicker
                        value={timeValue}
                        onChange={handleTimeChange}
                        placeholder={t('forms.pickATime')}
                        format="12h"
                        minTime={getCurrentTime()}
                        maxTime={getMidnightTime()}
                        timezone={question.timezone}
                        className="mt-1"
                    />
                );
            default:
                return <span>{t('forms.pleaseSelectValidDateType')}</span>;
        }
    };

    return (
        <div className="mb-4">
            <div className="main-label">
                {question.label} {question.required == 'yes' && <span className="text-red-500">*</span>}
            </div>
            {renderTimeInput()}
        </div>
    );
};